import React, { useState, useEffect, useCallback } from 'react'
import { supabase } from '../supabaseClient'
import { DAY_LABELS, fmtTime, dayOfWeekIST } from '../timeHelpers'
import { getTeacherAvailability, getTeacherConflictsInRange, hasAvailabilityOnWeekday } from '../availabilityHelper'

// 6am–11pm, one row per hour. The schema itself doesn't care about grid
// granularity (start_time/end_time are plain `time` columns) — this is
// purely an editing-UI choice, changeable later without a migration.
const HOURS = Array.from({ length: 17 }, (_, i) => i + 6) // [6,7,...,22]

function cellKey(day, hour) { return `${day}-${hour}` }

// Turn saved {day_of_week,start_time,end_time} rows into a Set of active
// "day-hour" cell keys for the grid.
function rowsToCells(rows) {
  const cells = new Set()
  for (const r of rows) {
    const startH = parseInt(r.start_time.slice(0, 2), 10)
    const endH   = parseInt(r.end_time.slice(0, 2), 10)
    for (let h = startH; h < endH; h++) cells.add(cellKey(r.day_of_week, h))
  }
  return cells
}

// Merge contiguous selected hours per day back into {day_of_week,start_time,end_time} ranges.
function cellsToRows(cells, teacherId, userId) {
  const rows = []
  for (let day = 0; day < 7; day++) {
    let runStart = null
    for (let h = 6; h <= 23; h++) {
      const active = h <= 22 && cells.has(cellKey(day, h))
      if (active && runStart === null) runStart = h
      if (!active && runStart !== null) {
        rows.push({
          teacher_id: teacherId, day_of_week: day,
          start_time: `${String(runStart).padStart(2, '0')}:00`,
          end_time:   `${String(h).padStart(2, '0')}:00`,
          created_by: userId,
        })
        runStart = null
      }
    }
  }
  return rows
}

// ── Reusable grid — used both by the admin editor below and by a
// teacher's own read-only "My Availability" view.
export function AvailabilityGrid({ teacherId, readOnly = false, onDirtyChange }) {
  const [cells,   setCells]   = useState(new Set())
  const [loading, setLoading] = useState(true)
  const [saving,  setSaving]  = useState(false)
  const [dirty,   setDirty]   = useState(false)
  const [ok,      setOk]      = useState('')
  const [err,     setErr]     = useState('')

  const load = useCallback(async () => {
    if (!teacherId) { setCells(new Set()); setLoading(false); return }
    setLoading(true)
    const rows = await getTeacherAvailability(teacherId)
    setCells(rowsToCells(rows))
    setDirty(false)
    setLoading(false)
  }, [teacherId])

  useEffect(() => { load() }, [load])
  useEffect(() => { onDirtyChange?.(dirty) }, [dirty, onDirtyChange])

  function toggle(day, hour) {
    if (readOnly) return
    setCells(prev => {
      const next = new Set(prev)
      const key = cellKey(day, hour)
      if (next.has(key)) next.delete(key); else next.add(key)
      return next
    })
    setDirty(true)
    setOk('')
  }

  async function save() {
    setSaving(true); setErr(''); setOk('')
    const { data: { user } } = await supabase.auth.getUser()
    const rows = cellsToRows(cells, teacherId, user?.id)

    // Simplest correct approach: replace this teacher's whole week in one
    // go rather than diffing — avoids any patch/merge edge cases.
    const { error: delErr } = await supabase.from('teacher_availability').delete().eq('teacher_id', teacherId)
    if (delErr) { setErr(delErr.message); setSaving(false); return }

    if (rows.length) {
      const { error: insErr } = await supabase.from('teacher_availability').insert(rows)
      if (insErr) { setErr(insErr.message); setSaving(false); return }
    }
    setOk('✓ Availability saved')
    setDirty(false)
    setSaving(false)
    setTimeout(() => setOk(''), 4000)
  }

  const cell = (active) => ({
    height: '30px', borderRadius: '6px', cursor: readOnly ? 'default' : 'pointer',
    border: `1px solid ${active ? 'rgba(16,185,129,0.5)' : 'rgba(var(--fg-rgb),0.08)'}`,
    background: active ? 'rgba(16,185,129,0.25)' : 'rgba(var(--fg-rgb),0.02)',
  })

  if (!teacherId) return <div style={{ textAlign:'center', padding:'2rem', color:'rgba(var(--fg-rgb),var(--a30))', fontSize:'13px' }}>Select a teacher.</div>
  if (loading) return <div style={{ textAlign:'center', padding:'2rem', color:'rgba(var(--fg-rgb),var(--a20))', fontSize:'13px' }}>Loading...</div>

  return (
    <div>
      <div style={{ display:'grid', gridTemplateColumns:'50px repeat(7,1fr)', gap:'4px', marginBottom: readOnly ? 0 : '14px' }}>
        <div/>
        {DAY_LABELS.map(d => (
          <div key={d} style={{ textAlign:'center', fontSize:'11px', fontWeight:700, color:'rgba(var(--fg-rgb),var(--a40))', paddingBottom:'4px' }}>{d}</div>
        ))}
        {HOURS.map(h => (
          <React.Fragment key={h}>
            <div style={{ fontSize:'10px', color:'rgba(var(--fg-rgb),var(--a30))', textAlign:'right', paddingRight:'6px', lineHeight:'30px' }}>
              {h % 12 === 0 ? 12 : h % 12}{h < 12 ? 'am' : 'pm'}
            </div>
            {DAY_LABELS.map((_, day) => {
              const active = cells.has(cellKey(day, h))
              return (
                <button key={day} type="button" onClick={() => toggle(day, h)} title={active ? 'Working' : 'Off'}
                  style={cell(active)}/>
              )
            })}
          </React.Fragment>
        ))}
      </div>

      {!readOnly && (
        <>
          {err && <div style={{ padding:'8px 12px', background:'rgba(239,68,68,0.1)', color:'#f87171', borderRadius:'8px', fontSize:'12px', marginBottom:'10px' }}>{err}</div>}
          {ok  && <div style={{ padding:'8px 12px', background:'rgba(16,185,129,0.1)', color:'#34d399', borderRadius:'8px', fontSize:'12px', marginBottom:'10px' }}>{ok}</div>}
          <button onClick={save} disabled={saving || !dirty}
            style={{ padding:'10px 22px', borderRadius:'10px', border:'none', fontSize:'13px', fontWeight:800, cursor: saving||!dirty ? 'not-allowed' : 'pointer', fontFamily:'inherit',
              background: saving||!dirty ? 'rgba(100,100,100,0.3)' : 'linear-gradient(135deg,#1e90ff,#0ea5e9)', color:'#fff' }}>
            {saving ? '⏳ Saving...' : dirty ? '💾 Save Availability' : '✓ Saved'}
          </button>
        </>
      )}
    </div>
  )
}

// ── Monthly overview — a real calendar month showing, per date: whether
// it's one of the teacher's working weekdays, and how many classes they
// already have booked that day. Click a date for the daily breakdown
// (which hours are open that weekday + the specific booked classes).
function MonthlyAvailabilityView({ teacherId }) {
  const today = new Date()
  const [viewYear,  setViewYear]  = useState(today.getFullYear())
  const [viewMonth, setViewMonth] = useState(today.getMonth())
  const [availRows, setAvailRows] = useState([])
  const [classes,   setClasses]   = useState([])
  const [loading,   setLoading]   = useState(true)
  const [selected,  setSelected]  = useState(null)

  const pad = n => String(n).padStart(2, '0')
  const monthStart = `${viewYear}-${pad(viewMonth + 1)}-01`
  const monthEnd   = `${viewYear}-${pad(viewMonth + 1)}-${pad(new Date(viewYear, viewMonth + 1, 0).getDate())}`

  const load = useCallback(async () => {
    if (!teacherId) return
    setLoading(true)
    const [avail, cls] = await Promise.all([
      getTeacherAvailability(teacherId),
      getTeacherConflictsInRange({ teacherId, fromDate: monthStart, toDate: monthEnd }),
    ])
    setAvailRows(avail)
    setClasses(cls)
    setSelected(null)
    setLoading(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teacherId, monthStart, monthEnd])

  useEffect(() => { load() }, [load])

  if (!teacherId) return <div style={{ textAlign:'center', padding:'2rem', color:'rgba(var(--fg-rgb),var(--a30))', fontSize:'13px' }}>Select a teacher.</div>
  if (loading) return <div style={{ textAlign:'center', padding:'2rem', color:'rgba(var(--fg-rgb),var(--a20))', fontSize:'13px' }}>Loading...</div>

  const firstDay    = new Date(viewYear, viewMonth, 1).getDay()
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate()
  const cells = []
  for (let i = 0; i < firstDay; i++) cells.push(null)
  for (let d = 1; d <= daysInMonth; d++) cells.push(d)

  function dateStr(d) { return `${viewYear}-${pad(viewMonth + 1)}-${pad(d)}` }
  function classesOn(ds) { return classes.filter(c => c.class_date === ds) }

  const todayStr = today.toISOString().slice(0, 10)
  const selectedClasses = selected ? classesOn(selected) : []
  const selectedWindows = selected ? availRows.filter(r => r.day_of_week === dayOfWeekIST(selected)) : []

  return (
    <div style={{ display:'grid', gridTemplateColumns:'1fr 320px', gap:'20px' }}>
      <div>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'12px' }}>
          <button type="button" onClick={() => { if(viewMonth===0){setViewMonth(11);setViewYear(y=>y-1)}else setViewMonth(m=>m-1) }}
            style={{ background:'rgba(var(--fg-rgb),0.06)', border:'none', color:'rgba(var(--fg-rgb),var(--a50))', borderRadius:'8px', width:'30px', height:'30px', cursor:'pointer', fontSize:'14px' }}>‹</button>
          <div style={{ fontSize:'14px', fontWeight:700, color:'rgba(var(--fg-rgb),var(--a80))' }}>
            {new Date(viewYear, viewMonth).toLocaleString('en-IN', { month:'long', year:'numeric' })}
          </div>
          <button type="button" onClick={() => { if(viewMonth===11){setViewMonth(0);setViewYear(y=>y+1)}else setViewMonth(m=>m+1) }}
            style={{ background:'rgba(var(--fg-rgb),0.06)', border:'none', color:'rgba(var(--fg-rgb),var(--a50))', borderRadius:'8px', width:'30px', height:'30px', cursor:'pointer', fontSize:'14px' }}>›</button>
        </div>

        <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:'4px', marginBottom:'4px' }}>
          {['S','M','T','W','T','F','S'].map((d,i) => (
            <div key={i} style={{ textAlign:'center', fontSize:'10px', fontWeight:700, color:'rgba(var(--fg-rgb),var(--a25))', padding:'4px 0' }}>{d}</div>
          ))}
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:'4px' }}>
          {cells.map((d, i) => {
            if (!d) return <div key={i}/>
            const ds        = dateStr(d)
            const isToday    = ds === todayStr
            const isSel      = ds === selected
            const working    = hasAvailabilityOnWeekday(availRows, new Date(ds+'T00:00:00').getDay())
            const bookedCount = classesOn(ds).length
            return (
              <button key={i} type="button" onClick={() => setSelected(ds)}
                style={{
                  padding:'8px 2px', minHeight:'56px', borderRadius:'8px', border: isToday ? '1px solid rgba(239,68,68,0.4)' : 'none', cursor:'pointer',
                  background: isSel ? '#1e90ff' : working ? 'rgba(16,185,129,0.08)' : 'rgba(var(--fg-rgb),0.02)',
                  color: isSel ? '#fff' : working ? '#34d399' : 'rgba(var(--fg-rgb),var(--a25))',
                  fontSize:'12px', fontWeight: working ? 700 : 400, display:'flex', flexDirection:'column', alignItems:'center', gap:'4px',
                }}>
                {d}
                {bookedCount > 0 && (
                  <span style={{ fontSize:'9px', fontWeight:700, padding:'1px 6px', borderRadius:'6px', background: isSel ? 'rgba(var(--fg-rgb),var(--a25))' : 'rgba(244,163,53,0.2)', color: isSel ? '#fff' : '#f4a335' }}>
                    {bookedCount} filled
                  </span>
                )}
              </button>
            )
          })}
        </div>

        <div style={{ display:'flex', gap:'14px', marginTop:'14px', fontSize:'10px', color:'rgba(var(--fg-rgb),var(--a30))' }}>
          <span><span style={{ color:'#34d399' }}>●</span> Working day</span>
          <span><span style={{ color:'rgba(var(--fg-rgb),var(--a25))' }}>●</span> Day off</span>
          <span><span style={{ color:'#f4a335' }}>●</span> Has booked class(es)</span>
        </div>
      </div>

      <div style={{ background:'rgba(var(--fg-rgb),0.04)', border:'0.5px solid rgba(var(--fg-rgb),0.07)', borderRadius:'14px', padding:'16px' }}>
        <div style={{ fontSize:'10px', fontWeight:700, color:'rgba(var(--fg-rgb),var(--a30))', letterSpacing:'0.08em', textTransform:'uppercase', marginBottom:'10px' }}>
          {selected ? new Date(selected+'T00:00:00').toLocaleDateString('en-IN',{weekday:'long',day:'numeric',month:'short'}) : 'Pick a date'}
        </div>
        {!selected ? (
          <div style={{ fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a25))' }}>Click a day on the calendar to see its hours and booked classes.</div>
        ) : (
          <>
            <div style={{ fontSize:'10px', fontWeight:700, color:'rgba(var(--fg-rgb),var(--a35))', marginBottom:'6px' }}>WORKING HOURS</div>
            {selectedWindows.length === 0 ? (
              <div style={{ fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a25))', marginBottom:'16px' }}>Day off — no hours set.</div>
            ) : (
              <div style={{ display:'flex', flexWrap:'wrap', gap:'6px', marginBottom:'16px' }}>
                {selectedWindows.map((w,i) => (
                  <span key={i} style={{ fontSize:'11px', fontWeight:700, padding:'4px 10px', borderRadius:'7px', background:'rgba(16,185,129,0.12)', color:'#34d399' }}>
                    {fmtTime(w.start_time)}–{fmtTime(w.end_time)}
                  </span>
                ))}
              </div>
            )}

            <div style={{ fontSize:'10px', fontWeight:700, color:'rgba(var(--fg-rgb),var(--a35))', marginBottom:'6px' }}>BOOKED CLASSES ({selectedClasses.length})</div>
            {selectedClasses.length === 0 ? (
              <div style={{ fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a25))' }}>Nothing booked yet.</div>
            ) : (
              <div style={{ display:'grid', gap:'6px' }}>
                {selectedClasses.sort((a,b)=>(a.start_time||'').localeCompare(b.start_time||'')).map(c => (
                  <div key={c.id} style={{ padding:'8px 10px', background:'rgba(244,163,53,0.08)', border:'0.5px solid rgba(244,163,53,0.2)', borderRadius:'8px' }}>
                    <div style={{ fontSize:'12px', fontWeight:600, color:'rgba(var(--fg-rgb),var(--a80))' }}>{c.title}</div>
                    <div style={{ fontSize:'11px', color:'#f4a335', marginTop:'2px' }}>{fmtTime(c.start_time)} · {c.duration_minutes || 60}m</div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

// ── Admin editor — pick a teacher, view the month, edit their week ───────
export default function TeacherAvailability({ profile }) {
  const [teachers,  setTeachers]  = useState([])
  const [teacherId, setTeacherId] = useState('')
  const [view,       setView]     = useState('monthly') // monthly | weekly

  useEffect(() => {
    supabase.from('profiles').select('id,full_name').eq('role', 'teacher').order('full_name')
      .then(({ data }) => {
        setTeachers(data || [])
        if (data?.length && !teacherId) setTeacherId(data[0].id)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const inp = { width:'320px', background:'var(--bg-input)', border:'0.5px solid rgba(var(--fg-rgb),0.1)', borderRadius:'10px', padding:'10px 13px', fontSize:'13px', color:'rgba(var(--fg-rgb),var(--a85))', outline:'none', fontFamily:'inherit', colorScheme:'dark' }

  return (
    <div style={{ marginTop:'14px' }}>
      <div style={{ fontSize:'16px', fontWeight:700, color:'rgba(var(--fg-rgb),var(--a85))', marginBottom:'4px' }}>🗓 Teacher Availability</div>
      <div style={{ fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a30))', marginBottom:'16px' }}>
        Set each teacher's recurring weekly working hours (IST) — used to warn/block scheduling outside these hours or into a class they already have.
      </div>

      <div style={{ display:'flex', gap:'16px', alignItems:'flex-end', marginBottom:'20px', flexWrap:'wrap' }}>
        <div>
          <label style={{ display:'block', fontSize:'10px', fontWeight:700, color:'rgba(var(--fg-rgb),var(--a30))', letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:'6px' }}>Teacher</label>
          <select style={inp} value={teacherId} onChange={e => setTeacherId(e.target.value)}>
            {teachers.map(t => <option key={t.id} value={t.id}>{t.full_name}</option>)}
          </select>
        </div>
        <div style={{ display:'flex', gap:'6px' }}>
          {[{ id:'monthly', label:'📅 Monthly' }, { id:'weekly', label:'🕐 Weekly Hours' }].map(v => (
            <button key={v.id} type="button" onClick={() => setView(v.id)} style={{
              fontSize:'12px', fontWeight:700, padding:'9px 16px', borderRadius:'10px', border:'none', cursor:'pointer', fontFamily:'inherit',
              background: view===v.id ? '#1e90ff' : 'rgba(var(--fg-rgb),0.06)', color: view===v.id ? '#fff' : 'rgba(var(--fg-rgb),var(--a45))',
            }}>{v.label}</button>
          ))}
        </div>
      </div>

      <div style={{ background:'rgba(var(--fg-rgb),0.04)', border:'0.5px solid rgba(var(--fg-rgb),0.07)', borderRadius:'14px', padding:'20px' }}>
        {!teacherId ? (
          <div style={{ textAlign:'center', padding:'2rem', color:'rgba(var(--fg-rgb),var(--a30))', fontSize:'13px' }}>No teachers found.</div>
        ) : view === 'monthly' ? (
          <MonthlyAvailabilityView teacherId={teacherId}/>
        ) : (
          <AvailabilityGrid teacherId={teacherId}/>
        )}
      </div>
    </div>
  )
}
